import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

const ACCOUNTS_COLLECTION = 'accounts';
const EXPENSES_SUBCOLLECTION = 'expenses';
/** Partner expenses that belong to BBA itself rather than a client account. */
const INTERNAL_EXPENSES_COLLECTION = 'internalExpenses';

/** Same three states the Billing tab uses for payments, so both tables read alike. */
export const EXPENSE_STATUSES = ['paid', 'processing', 'failed'];

export function normalizeExpenseStatus(status) {
  const s = String(status || '').toLowerCase().trim();
  if (s === 'paid' || s === 'succeeded' || s === 'complete' || s === 'completed') return 'paid';
  if (s === 'failed' || s === 'overdue' || s === 'declined') return 'failed';
  return 'processing';
}

/** Expense date → Date, tolerating 'YYYY-MM-DD', ISO strings and Firestore timestamps. */
export function parseExpenseDate(expense) {
  const raw = expense?.date || expense?.createdAt;
  if (!raw) return null;
  if (raw.toDate) return raw.toDate();
  if (raw instanceof Date) return raw;
  if (typeof raw === 'string') {
    // Plain calendar dates must stay local — 'YYYY-MM-DD' parses as UTC otherwise
    const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const dt = new Date(raw);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  return null;
}

function normalizeExpenseDoc(id, data) {
  return {
    id,
    amount: Number(data?.amount) || 0,
    date: data?.date || '',
    status: normalizeExpenseStatus(data?.status),
    subcontractorId: data?.subcontractorId || '',
    subcontractorName: data?.subcontractorName || '',
    description: data?.description || '',
    source: data?.source || 'manual',
    createdAt: data?.createdAt || null,
    updatedAt: data?.updatedAt || null,
  };
}

/**
 * All expenses for an account, newest first.
 * Sorted in memory rather than with orderBy so records written without a
 * createdAt (older webhook payloads) are still returned.
 */
export async function getAccountExpenses(accountId) {
  if (!accountId) return [];
  try {
    const snap = await getDocs(
      collection(db, ACCOUNTS_COLLECTION, accountId, EXPENSES_SUBCOLLECTION)
    );
    const list = snap.docs.map((d) => normalizeExpenseDoc(d.id, d.data()));
    list.sort((a, b) => {
      const da = parseExpenseDate(a);
      const dbb = parseExpenseDate(b);
      return (dbb ? dbb.getTime() : 0) - (da ? da.getTime() : 0);
    });
    return list;
  } catch (error) {
    console.error('Error getting account expenses:', error);
    return [];
  }
}

async function addExpenseTo(collectionRef, expense) {
  const payload = {
    amount: Math.abs(Number(expense?.amount) || 0),
    date: expense?.date || '',
    status: normalizeExpenseStatus(expense?.status),
    subcontractorId: expense?.subcontractorId || '',
    subcontractorName: expense?.subcontractorName || '',
    description: expense?.description || '',
    source: expense?.source || 'manual',
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };
  const ref = await addDoc(collectionRef, payload);
  return { id: ref.id, ...payload };
}

async function updateExpenseAt(docRef, patch) {
  const cleaned = {};
  if (patch.amount !== undefined) cleaned.amount = Math.abs(Number(patch.amount) || 0);
  if (patch.date !== undefined) cleaned.date = patch.date || '';
  if (patch.status !== undefined) cleaned.status = normalizeExpenseStatus(patch.status);
  if (patch.subcontractorId !== undefined) cleaned.subcontractorId = patch.subcontractorId || '';
  if (patch.subcontractorName !== undefined) cleaned.subcontractorName = patch.subcontractorName || '';
  if (patch.description !== undefined) cleaned.description = patch.description || '';
  cleaned.updatedAt = Timestamp.now();
  await updateDoc(docRef, cleaned);
}

export async function addAccountExpense(accountId, expense) {
  return addExpenseTo(collection(db, ACCOUNTS_COLLECTION, accountId, EXPENSES_SUBCOLLECTION), expense);
}

export async function updateAccountExpense(accountId, expenseId, patch) {
  await updateExpenseAt(doc(db, ACCOUNTS_COLLECTION, accountId, EXPENSES_SUBCOLLECTION, expenseId), patch);
}

export async function deleteAccountExpense(accountId, expenseId) {
  await deleteDoc(doc(db, ACCOUNTS_COLLECTION, accountId, EXPENSES_SUBCOLLECTION, expenseId));
}

/** Every internal (non-client) expense, unsorted — the Finance Reports P&L buckets them by month. */
export async function getInternalExpenses() {
  try {
    const snap = await getDocs(collection(db, INTERNAL_EXPENSES_COLLECTION));
    return snap.docs.map((d) => ({ ...normalizeExpenseDoc(d.id, d.data()), accountId: '' }));
  } catch (error) {
    console.error('Error getting internal expenses:', error);
    return [];
  }
}

// ---- Partner view (CRM Partner Overview ▸ Billing) ----
// Rows carry accountId: a client account id, or '' for an internal expense
// (stored in the top-level internalExpenses collection, same shape).

/**
 * Every expense recorded against a partner: Billing-tab expenses on the given
 * client accounts whose subcontractor is this partner, plus internal (non-client)
 * expenses, newest first.
 *
 * Each account's expenses subcollection is read and filtered in memory: a
 * collection-group read is denied by the catch-all Firestore rule, and in-memory
 * matching also catches webhook expenses that only recorded the partner's name.
 */
export async function getPartnerExpenses(partnerId, partnerName = '', accountIds = []) {
  if (!partnerId) return [];
  const nameKey = String(partnerName || '').trim().toLowerCase();
  const matchesPartner = (e) =>
    e.subcontractorId
      ? e.subcontractorId === partnerId
      : Boolean(nameKey) && e.subcontractorName.trim().toLowerCase() === nameKey;

  const [internalSnap, ...accountResults] = await Promise.all([
    getDocs(
      query(collection(db, INTERNAL_EXPENSES_COLLECTION), where('subcontractorId', '==', partnerId))
    ),
    // One unreadable account shouldn't blank the whole table
    ...accountIds.map((accountId) =>
      getDocs(collection(db, ACCOUNTS_COLLECTION, accountId, EXPENSES_SUBCOLLECTION))
        .then((snap) => ({ accountId, snap }))
        .catch((error) => {
          console.error(`Error reading expenses for account ${accountId}:`, error);
          return null;
        })
    ),
  ]);

  const clientRows = accountResults
    .filter(Boolean)
    .flatMap(({ accountId, snap }) =>
      snap.docs.map((d) => ({ ...normalizeExpenseDoc(d.id, d.data()), accountId }))
    )
    .filter(matchesPartner);
  const internalRows = internalSnap.docs.map((d) => ({
    ...normalizeExpenseDoc(d.id, d.data()),
    accountId: '',
  }));

  const list = [...clientRows, ...internalRows];
  list.sort((a, b) => {
    const da = parseExpenseDate(a);
    const dbb = parseExpenseDate(b);
    return (dbb ? dbb.getTime() : 0) - (da ? da.getTime() : 0);
  });
  return list;
}

function partnerExpenseDocRef(expense) {
  return expense.accountId
    ? doc(db, ACCOUNTS_COLLECTION, expense.accountId, EXPENSES_SUBCOLLECTION, expense.id)
    : doc(db, INTERNAL_EXPENSES_COLLECTION, expense.id);
}

/**
 * Create or update a partner-view expense under `accountId` ('' = internal).
 * When an edit moves it to a different client, or between a client and internal,
 * the record is re-created in its new home and the old one deleted.
 */
export async function savePartnerExpense(expense, accountId, original = null) {
  const target = accountId || '';
  if (original && (original.accountId || '') === target) {
    await updateExpenseAt(partnerExpenseDocRef(original), expense);
    return;
  }
  const collectionRef = target
    ? collection(db, ACCOUNTS_COLLECTION, target, EXPENSES_SUBCOLLECTION)
    : collection(db, INTERNAL_EXPENSES_COLLECTION);
  await addExpenseTo(collectionRef, original ? { ...expense, source: original.source } : expense);
  if (original) await deleteDoc(partnerExpenseDocRef(original));
}

export async function deletePartnerExpense(expense) {
  if (!expense?.id) return;
  await deleteDoc(partnerExpenseDocRef(expense));
}

/** Sum of expenses that actually left the account (status paid). */
export function sumExpenses(expenses = []) {
  return expenses
    .filter((e) => normalizeExpenseStatus(e.status) === 'paid')
    .reduce((sum, e) => sum + Math.abs(Number(e.amount) || 0), 0);
}

/** Paid expenses dated inside the given month (defaults to the current month). */
export function sumExpensesForMonth(expenses = [], reference = new Date()) {
  const year = reference.getFullYear();
  const month = reference.getMonth();
  return sumExpenses(
    expenses.filter((e) => {
      const d = parseExpenseDate(e);
      return d && d.getFullYear() === year && d.getMonth() === month;
    })
  );
}
